// @ts-nocheck
import { useMemo, useRef } from "react";
import { useNavigation, useRouteLoaderData, useSearchParams } from "react-router-dom";
//-------------------------------------------
import {
    MyDate,
    getDatefromWeekNum,
    Select,
    fleetUsageStartWeeks as weeks,
} from "src/components/common/functions";
import Chart from "./chart";
//-------------------------------------------
export default function FleetUsage(){
    const rawLoaderData = useRouteLoaderData("FleetUsage");
    const loaderData = useMemo(() => rawLoaderData ?? {}, [rawLoaderData]);
    const navigation = useNavigation();
    const [searchParams, setSearchParams] = useSearchParams();
    const myChart = useRef(null);
    const onComplete = () => {
        const chartInstance = myChart.current?.chart;
        if(!chartInstance) return;
        const ctx = chartInstance.ctx;
        if(!ctx) return;
        ctx.font = Chart.helpers?.fontString(Chart.defaults.global.defaultFontSize, Chart.defaults.global.defaultFontStyle, Chart.defaults.global.defaultFontFamily) ?? ctx.font;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        myChart.current.data.datasets.forEach((dataset, i) => {
            const meta = chartInstance.controller.getDatasetMeta(i);
            meta.data.forEach((bar, index) => {
                const data = dataset.data[index];
                if(data > 0){
                    ctx.fillText(data, bar._model.x, bar._model.y - 5);
                }
            });
        });
    };
    const opt = [
        [255, 99, 132],
        [54, 162, 235],
        [255, 206, 86],
        [75, 192, 192],
        [153, 102, 255],
        [255, 159, 64],
        [127, 188, 65],
        [50, 136, 189],
        [94, 79, 162],
    ];
    const active = searchParams.get("mode") || loaderData.mode || "Month";
    const month = searchParams.get("month") || loaderData.month || new MyDate().s().slice(0, 7);
    const week = searchParams.get("week") || loaderData.week || "";
    const showLoading = navigation.state === "loading";
    const truckNumbers = useMemo(() => Array.isArray(loaderData.fleetOptions) ? loaderData.fleetOptions : [], [loaderData]);
    const usageCounts = useMemo(() => Array.isArray(loaderData.usageCounts) ? loaderData.usageCounts : [], [loaderData]);
    const monthlyTrend = useMemo(() => Array.isArray(loaderData.monthlyTrend) ? loaderData.monthlyTrend : [], [loaderData]);
    const selectedFleetNumbers = useMemo(
        () => Array.isArray(loaderData.selectedFleetNumbers) ? loaderData.selectedFleetNumbers.map(String) : [],
        [loaderData]
    );
    const selectedTrendFleetNumbers = useMemo(
        () => Array.isArray(loaderData.selectedTrendFleetNumbers) ? loaderData.selectedTrendFleetNumbers.map(String) : [],
        [loaderData]
    );
    const truckNumber = useMemo(
        () => truckNumbers.filter(option => selectedFleetNumbers.includes(String(option.value))),
        [truckNumbers, selectedFleetNumbers]
    );
    const singleTruckNumbers = useMemo(
        () => truckNumbers.filter(option => selectedTrendFleetNumbers.includes(String(option.value))),
        [truckNumbers, selectedTrendFleetNumbers]
    );
    const usageCountMap = useMemo(
        () => Object.fromEntries(usageCounts.map(item => [String(item.fleetNumber), Number(item.count || 0)])),
        [usageCounts]
    );
    const trendRange = useMemo(
        () => buildMonthRange(loaderData.trendStart, loaderData.trendEnd, active === "Month" ? month : loaderData.start?.slice(0, 7)),
        [active, loaderData.start, loaderData.trendEnd, loaderData.trendStart, month]
    );
    const singleData = useMemo(() => {
        const output = {};
        const selected = monthlyTrend.filter(item => selectedTrendFleetNumbers.includes(String(item.fleetNumber)));
        const keys = trendRange.length ? trendRange : selected.map(item => item.month).filter(Boolean).sort();
        const min = keys[0] || month;
        const max = keys[keys.length - 1] || month;
        if(!min || !max) return output;
        for (const fleetNumber of selectedTrendFleetNumbers) {
            output[fleetNumber] = {};
        }
        selected.forEach(item => {
            const fleetNumber = String(item.fleetNumber);
            if(!(fleetNumber in output)) output[fleetNumber] = {};
            output[fleetNumber][item.month] = Number(item.count || 0);
        });
        let cur = min;
        while(cur <= max){
            for (const key of Object.keys(output)) {
                if(!(cur in output[key])) output[key][cur] = 0;
            }
            const curYear = cur.slice(0, 4);
            const curMonth = cur.slice(5, 7);
            const nextYear = curMonth === "12" ? (window.parseInt(curYear) + 1).toString() : curYear;
            let nextMonth = window.parseInt(curMonth) + 1;
            if(nextMonth > 0 && nextMonth < 10){
                nextMonth = "0" + nextMonth.toString();
            }else if(nextMonth >= 10 && nextMonth <= 12){
                nextMonth = nextMonth.toString();
            }else if(nextMonth === 13){
                nextMonth = "01";
            }
            const next = nextYear + "-" + nextMonth;
            if(next === cur) break;
            cur = next;
        }
        Object.keys(output).forEach(key => {
            output[key] = Object.entries(output[key]).sort((a, b) => a[0] > b[0] ? 1 : -1);
        });
        return output;
    }, [month, monthlyTrend, selectedTrendFleetNumbers, trendRange]);
    const updateSearch = (updates, multi = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if(value === "" || value === undefined || value === null){
                next.delete(key);
            }else{
                next.set(key, String(value));
            }
        });
        Object.entries(multi).forEach(([key, values]) => {
            next.delete(key);
            values.forEach(value => next.append(key, String(value)));
        });
        setSearchParams(next);
    };
    const selectedUsageCounts = truckNumber.map(t => usageCountMap[String(t.value)] ?? 0);
    const selectedTrendValues = Object.values(singleData).flat().map(item => Number(item[1] || 0));
    return (
        <div className = "my-3 px-3">
            <div className = "d-flex align-items-center mb-3">
                <label className = "mb-0 me-2 alignSelf-center">Mode</label>
                <select
                    required
                    className = "me-3 form-control inline"
                    value = {active}
                    onChange = {e => updateSearch({ mode: e.currentTarget.value })}
                >
                    <option value = "">Select</option>
                    <option>Week</option>
                    <option>Month</option>
                </select>
                {active === "Month" ?
                    <input
                        required
                        className = "form-control inline"
                        type = "month"
                        max = {(new MyDate()).s().slice(0, 7)}
                        value = {month}
                        onChange = {e => updateSearch({ month: e.currentTarget.value })}
                    />
                    : null}
                {active === "Week" ?
                    <select
                        required
                        className = "form-control inline"
                        value = {week}
                        onChange = {e => updateSearch({ week: e.currentTarget.value })}
                    >
                        <option value = "">Select</option>
                        {weeks.map((wk, key) => <option key = {key} value = {wk}>{wk}</option>)}
                    </select>
                    : null}
            </div>
            <div className = "d-flex align-items-center">
                <label className = "mb-0 alignSelf-center">Vehicles</label>
                <div className = "mx-3 flex-grow-1">
                    <Select
                        isMulti
                        value = {truckNumber}
                        onChange = {e => {
                            updateSearch({}, { fleetNumber: (e ?? []).map(option => option.value) });
                        }}
                        options = {truckNumbers}
                    />
                </div>
            </div>
            {((active === "Month" && month) || (active === "Week" && week)) && truckNumber.length ?
                <div className = "flex-grow-1">
                    <Chart
                        myChart = {myChart}
                        chartConfig = {{
                            type: "bar",
                            data: {
                                labels: truckNumber.map(t => t.value),
                                datasets: truckNumber
                                    .map(t => ({ label: t.label, data: usageCountMap[String(t.value)] ?? 0 }))
                                    .map((item, itemIndex) => ({
                                        data: [...Array(itemIndex).fill(0), item.data],
                                        label: item.label,
                                        backgroundColor: `rgba(${opt[itemIndex % 9].join(", ")}, 0.5)`,
                                        borderColor: `rgba(${opt[itemIndex % 9].join(", ")}, 1)`,
                                        fill: false,
                                    }))
                            },
                            options: {
                                animation: { onComplete },
                                hover: {
                                    animationDuration: 0
                                },
                                legend: {
                                    display: true
                                },
                                title: {
                                    fontSize: 20,
                                    display: true,
                                    text: `Fleet Usage on ${active === "Month" ? month : ""}${active === "Week" ? `${week} (${getDatefromWeekNum(week, 0)} ~ ${getDatefromWeekNum(week, 6)})` : ""}`
                                },
                                scales: {
                                    xAxes: [{
                                        stacked: true,
                                        scaleLabel: {
                                            fontSize: 15,
                                            display: true,
                                            labelString: "Vehicle"
                                        }
                                    }],
                                    yAxes: [{
                                        stacked: true,
                                        ticks: {
                                            max: Math.ceil(1.1 * Math.max(...selectedUsageCounts, 0)),
                                            beginAtZero: true,
                                            callback: value => { if(value % 1 === 0) return value; }
                                        },
                                        scaleLabel: {
                                            fontSize: 15,
                                            display: true,
                                            labelString: "Number of Times"
                                        },
                                    }]
                                }
                            }
                        }}
                    />
                </div>
                : null}
            <div className = "mt-4 d-flex align-items-center">
                <label className = "mb-0 alignSelf-center">Vehicles</label>
                <div className = "mx-3 flex-grow-1">
                    <Select
                        isMulti
                        value = {singleTruckNumbers}
                        onChange = {e => {
                            updateSearch({}, { trendFleetNumber: (e ?? []).map(option => option.value) });
                        }}
                        options = {truckNumbers}
                    />
                </div>
            </div>
            {singleTruckNumbers.length ?
                <div className = "flex-grow-1">
                    <Chart
                        myChart = {myChart}
                        chartConfig = {{
                            type: "line",
                            data: {
                                labels: Object.values(singleData)[0]?.map(item => item[0]),
                                datasets: Object.entries(singleData).map((entry, entryIndex) => ({
                                    label: entry[0],
                                    data: entry[1].map(item => item[1]),
                                    tension: 0,
                                    fill: false,
                                    backgroundColor: `rgba(${opt[entryIndex % 9].join(", ")}, 0.5)`,
                                    borderColor: `rgba(${opt[entryIndex % 9].join(", ")}, 1)`,
                                }))
                            },
                            options: {
                                hover: {
                                    animationDuration: 0
                                },
                                legend: {
                                    display: true
                                },
                                title: {
                                    fontSize: 20,
                                    display: true,
                                    text: "Fleet Usage"
                                },
                                scales: {
                                    xAxes: [{
                                        scaleLabel: {
                                            fontSize: 15,
                                            display: true,
                                            labelString: "Month"
                                        }
                                    }],
                                    yAxes: [{
                                        ticks: {
                                            max: Math.ceil(1.1 * Math.max(...selectedTrendValues, 0)),
                                            beginAtZero: true,
                                            callback: value => { if(value % 1 === 0) return value; }
                                        },
                                        scaleLabel: {
                                            fontSize: 15,
                                            display: true,
                                            labelString: "Number of Times"
                                        }
                                    }]
                                }
                            }
                        }}
                    />
                </div>
                : null}
            {showLoading ? <div className = "cssLoading">Loading...</div> : null}
        </div>
    );
}

function buildMonthRange(start, end, fallbackMonth){
    const normalizedStart = normalizeMonthValue(start);
    const normalizedEnd = normalizeMonthValue(end) || normalizeMonthValue(fallbackMonth);
    if(!normalizedEnd) return [];
    const firstMonth = normalizedStart || shiftMonth(normalizedEnd, -11);
    const output = [];
    let current = firstMonth;
    while(current <= normalizedEnd){
        output.push(current);
        const next = shiftMonth(current, 1);
        if(next === current) break;
        current = next;
    }
    return output;
}

function normalizeMonthValue(value){
    if(!value) return null;
    const match = String(value).match(/^(\d{4}-\d{2})/);
    return match ? match[1] : null;
}

function shiftMonth(month, amount){
    const [yearValue, monthValue] = String(month).split("-");
    const year = Number(yearValue);
    const monthIndex = Number(monthValue) - 1;
    if(Number.isNaN(year) || Number.isNaN(monthIndex)) return month;
    const date = new Date(year, monthIndex, 1);
    date.setMonth(date.getMonth() + amount);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
